/**
 * The active workspace's newest knowledge-graph build, for the canvas status pill.
 *
 * Builds start on their own when the workspace's data changes, so the canvas no longer
 * tracks a run it started: it polls the newest one (every 10s while a build runs, every
 * 60s otherwise) and refreshes the graph when a build it has seen start, finishes.
 *
 * Organism/page level only (owns React Query).
 */

import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import { fetchLatestPipelineRun } from "../services/pipeline/pipelineApi";
import {
  deriveLatestRunPhase,
  finishedRunKey,
  isTerminalPipelineStatus,
  type KgPipelinePhase,
} from "../services/pipeline/pipelineRunState";
import { tryGetActiveWorkspaceId } from "../services/workspace/activeWorkspace";

export const ACTIVE_BUILD_POLL_MS = 10_000;
export const IDLE_POLL_MS = 60_000;

export interface UseKgLatestPipelineRunResult {
  phase: KgPipelinePhase;
  /** Why the newest build failed. */
  errorMessage?: string;
  /** completed_at of the newest build, for the "Graph updated · time" pill. */
  completedAt?: string | null;
  /** Set when the status itself could not be read and nothing is known yet. */
  unavailableMessage?: string;
}

export const useKgLatestPipelineRun = (): UseKgLatestPipelineRunResult => {
  const queryClient = useQueryClient();
  const workspaceId = tryGetActiveWorkspaceId();

  const latestRun = useQuery({
    queryKey: queryKeys.pipeline.latest(workspaceId ?? "none"),
    queryFn: () => fetchLatestPipelineRun(workspaceId as string),
    enabled: Boolean(workspaceId),
    refetchInterval: (query) => {
      const run = query.state.data;
      return run && !isTerminalPipelineStatus(run.status)
        ? ACTIVE_BUILD_POLL_MS
        : IDLE_POLL_MS;
    },
    retry: false,
  });

  const run = latestRun.data;
  const finished = finishedRunKey(run);
  // The newest finished build seen for this workspace. Unset until its first answer: the
  // graph on screen was fetched by the canvas itself, so only a build finishing after
  // that warrants a refresh.
  const lastSeen = useRef<{ workspaceId: string; finished: string | null }>();

  useEffect(() => {
    if (!latestRun.isSuccess || !workspaceId) {
      return;
    }
    const previous =
      lastSeen.current?.workspaceId === workspaceId ? lastSeen.current : undefined;
    lastSeen.current = { workspaceId, finished };
    // A failed build can still have written part of the canvas, so any finish counts.
    if (previous && finished !== null && finished !== previous.finished) {
      queryClient.invalidateQueries({ queryKey: queryKeys.kg.graphRoot(workspaceId) });
    }
  }, [finished, latestRun.isSuccess, workspaceId, queryClient]);

  const { phase, errorMessage } = deriveLatestRunPhase(run);
  return {
    phase,
    errorMessage,
    completedAt: run?.completed_at,
    unavailableMessage:
      latestRun.isError && run === undefined ? latestRun.error.message : undefined,
  };
};
